import {Stack, Text} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';

import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import PlateSchemaSelector from 'samples/PlateSchemaSelector';
import {PlateSchema, PlateSchemaType} from 'samples/storables/StorableSchemaConstants';

import PlateSizeSelector from './PlateSizeSelector';

export default function PlateSizeEditor({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const plateHeight = typeof config.plateHeight === 'number' ? config.plateHeight : null;
  const plateWidth = typeof config.plateWidth === 'number' ? config.plateWidth : null;
  const selectedPlateSchemaId = typeof config.plateSchemaId === 'string' ? config.plateSchemaId : null;
  const parsedPlateSize = useMemo(
    (): [number, number] | null =>
      plateHeight != null && plateWidth != null ? [plateHeight, plateWidth] : null,
    [plateHeight, plateWidth]
  );

  const plateSchemaFilter = useCallback(
    (schema: PlateSchema): boolean =>
      parsedPlateSize != null && schema.height === parsedPlateSize[0] && schema.width === parsedPlateSize[1],
    [parsedPlateSize]
  );

  return (
    <Stack spacing="xs">
      <PlateSizeSelector
        disabled={disabled}
        height={plateHeight}
        onChange={(height, width) => {
          let updatedConfig = setValueInConfig(config, ['plateHeight'], height);
          updatedConfig = setValueInConfig(updatedConfig, ['plateWidth'], width);
          updatedConfig = setValueInConfig(updatedConfig, ['plateSchemaId'], null);
          onChangeConfig(updatedConfig, ['plateHeight'], height);
        }}
        width={plateWidth}
      />
      <Stack spacing={4}>
        <Text fz="sm" fw={600}>
          Plate schema
        </Text>
        <PlateSchemaSelector
          compact={true}
          plateSchemaType={PlateSchemaType.FIXED_PLATE}
          plateSchemaFilter={parsedPlateSize == null ? undefined : plateSchemaFilter}
          onChange={(schema) => {
            onChangeConfig(config, ['plateSchemaId'], schema?.id ?? null);
          }}
          registryId={undefined}
          selectedPlateSchemaId={selectedPlateSchemaId}
          disabled={disabled || parsedPlateSize == null}
          disabledMessage={parsedPlateSize == null ? 'Select a plate size first' : undefined}
          shouldShowNoSchemaOption={true}
        />
      </Stack>
    </Stack>
  );
}
